import './style.css';
import { Engine } from '@babylonjs/core/Engines/engine';
import { createZoneJoystick } from './input/touchControls.js';
import { createDeviceTilt } from './input/useDeviceTilt.js';
import { enterFullscreen, lockLandscape, isFullscreenSupported } from './input/fullscreen.js';
import { createSimInput } from './input/simInput.js';
import { loadEngine } from './wasm/loadEngine.js';

import { TUTORIAL_LEVEL, TIMELINE } from './level/index.js';
import { createInitialState, step, DEFAULT_SIM_CONFIG, type SimEvent } from './sim/index.js';
import {
  createBallRoomState,
  stepBallRoom,
  DEFAULT_BALLROOM_CONFIG,
  type BallRoomEvent,
} from './sim/ballroom.js';
import {
  createCatRoomState,
  stepCatRoom,
  DEFAULT_CATROOM_CONFIG,
  type CatRoomEvent,
} from './sim/catroom.js';
import {
  createCrossingState,
  stepCrossing,
  DEFAULT_CROSSING_CONFIG,
  type CrossingEvent,
} from './sim/crossing.js';
import {
  createInterferenceState,
  stepInterference,
  DEFAULT_INTERFERENCE_CONFIG,
  type InterferenceEvent,
} from './sim/interference.js';
import { GameScene } from './scene/gameScene.js';
import { BallRoomScene } from './scene/ballRoomScene.js';
import { CatRoomScene } from './scene/catRoomScene.js';
import { CrossingScene } from './scene/crossingScene.js';
import { InterferenceScene } from './scene/interferenceScene.js';
import { createBoardPanel } from './ui/boardPanel.js';
import { createPromptHud } from './ui/promptHud.js';
import { createActionButtons } from './ui/actionButtons.js';
import { createSceneComplete } from './ui/sceneComplete.js';
import { createVoltageWheel } from './ui/voltageWheel.js';
import { createGameAudio } from './audio/index.js';

const app = document.getElementById('app') as HTMLElement;
const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
const leftZone = document.getElementById('left-zone') as HTMLElement;
const rightZone = document.getElementById('right-zone') as HTMLElement;
const tiltBtn = document.getElementById('tilt-btn') as HTMLButtonElement;
const startGate = document.getElementById('start-gate') as HTMLButtonElement;
const rotateHint = document.getElementById('rotate-hint') as HTMLElement;

// Mobile-only, landscape: nudge the player to rotate when in portrait.
const orientationQuery = window.matchMedia('(orientation: portrait)');
const updateOrientation = () => {
  rotateHint.hidden = !orientationQuery.matches;
};
orientationQuery.addEventListener('change', updateOrientation);
updateOrientation();

// --- shared input + UI ---
const moveStick = createZoneJoystick(leftZone);
const lookStick = createZoneJoystick(rightZone, { variant: 'look' });
const tilt = createDeviceTilt();
const simInput = createSimInput(moveStick, lookStick, tilt);
app.addEventListener('pointerdown', () => app.classList.add('engaged'), { once: true });

const boardPanel = createBoardPanel(app);
// The interact button's action is set per-scene.
let interactHandler: () => void = () => {};
const promptHud = createPromptHud(app, () => interactHandler());
const actionButtons = createActionButtons(app, {
  onJump: () => simInput.pressJump(),
  onCrouchStart: () => simInput.setCrouch(true),
  onCrouchEnd: () => simInput.setCrouch(false),
});
const sceneComplete = createSceneComplete(app);
const voltageWheel = createVoltageWheel(app);
const audio = createGameAudio();

/** Single reusable controls overlay — each scene declares its control set once. */
function setControls(c: {
  move?: boolean;
  look?: boolean;
  jump?: boolean;
  crouch?: boolean;
  wheel?: boolean;
}): void {
  leftZone.style.display = c.move ? '' : 'none';
  rightZone.style.display = c.look ? '' : 'none';
  actionButtons.jump.style.display = c.jump ? '' : 'none';
  actionButtons.crouch.style.display = c.crouch ? '' : 'none';
  voltageWheel.el.style.display = c.wheel ? 'flex' : 'none';
}

// One Engine for the whole app; scenes are swapped as Babylon Scenes on it
// (recreating an Engine on the same canvas is unreliable, esp. on iOS).
const engine = new Engine(canvas, true, { stencil: false });
const FIXED_DT = 1 / 120;

interface SceneController {
  frame(now: number): void;
  dispose(): void;
}
type SceneKind = 'runner' | 'ballroom' | 'catroom' | 'crossing' | 'interference';
let current: SceneController | null = null;
function setScene(kind: SceneKind): void {
  current?.dispose();
  sceneComplete.hide(); // never let the transition overlay linger into the next scene
  interactHandler = () => {};
  const starters: Record<SceneKind, () => SceneController> = {
    runner: startRunner,
    ballroom: startBallRoom,
    catroom: startCatRoom,
    crossing: startCrossing,
    interference: startInterference,
  };
  current = starters[kind]();
}

engine.runRenderLoop(() => current?.frame(performance.now()));

// =====================================================================
// Scene 1 — the runner (tutorial)
// =====================================================================
function startRunner(): SceneController {
  const level = TUTORIAL_LEVEL;
  const cfg = DEFAULT_SIM_CONFIG;
  const state = createInitialState(level, cfg);
  const scene = new GameScene(engine, level);
  interactHandler = () => simInput.pressInteract();
  setControls({ move: true, look: true, jump: true, crouch: true });

  let warnUntil = 0;
  let transientPrompt = 'Steer ◂ ▸ · JUMP to clear walls · hold CROUCH to slide under';
  let transientUntil = performance.now() + 5500;
  let blockedIdx = -1;
  let blockedSince = 0;
  let switchTimer: ReturnType<typeof setTimeout> | null = null;
  let acc = 0;
  let lastT = performance.now();
  const STUCK_MS = 1500;
  const OBSTACLE_HINT: Record<string, string> = {
    hurdle: 'Tap JUMP to leap over the wall!',
    bar: 'Hold CROUCH to slide underneath!',
    wall: 'Steer left or right to get around it →',
  };

  loadEngine()
    .then((e) => scene.setQubitCollapse((e.exports.collapse(42) >>> 0) / 0xffffffff))
    .catch((err: unknown) => console.error('engine failed to load', err));

  function handleEvents(events: SimEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'board-enter':
          boardPanel.show(TIMELINE[level.boards[e.index]!.timelineIndex]!);
          break;
        case 'board-exit':
          boardPanel.hide();
          break;
        case 'bump':
          warnUntil = performance.now() + 280;
          transientPrompt = 'Jump (flick up), crouch (hold down), or steer around';
          transientUntil = performance.now() + 2200;
          break;
        case 'handle-reject':
          transientPrompt = 'Too high to reach — try the handle in the corner →';
          transientUntil = performance.now() + 2800;
          break;
        case 'complete':
          sceneComplete.play();
          scene.startInterior(); // fall into the SDF bubble time-tunnel
          switchTimer = setTimeout(() => setScene('ballroom'), 3600);
          break;
        default:
          break;
      }
    }
    audio.handle(events);
  }

  function updatePrompts(now: number): void {
    app.classList.toggle('warn-flash', now < warnUntil);
    if (state.nearObstacle !== blockedIdx) {
      blockedIdx = state.nearObstacle;
      blockedSince = now;
    }
    if (state.complete) {
      promptHud.setPrompt(null);
      promptHud.showInteract(false);
      return;
    }
    if (state.nearHandle) {
      promptHud.showInteract(true);
      promptHud.setPrompt('Operate the handle');
      return;
    }
    promptHud.showInteract(false);
    if (blockedIdx !== -1 && now - blockedSince > STUCK_MS) {
      const kind = level.obstacles[blockedIdx]?.kind ?? 'wall';
      promptHud.setPrompt(OBSTACLE_HINT[kind] ?? OBSTACLE_HINT.wall!, true);
      return;
    }
    if (state.beamActive) {
      promptHud.setPrompt('Steer into the scanner beam to be scanned in');
    } else if (state.phase === 'atGate') {
      const stuck = blockedIdx === -1 && now - blockedSince > STUCK_MS;
      promptHud.setPrompt(
        'The gate is locked — find a handle to open it (look to the sides)',
        stuck,
      );
    } else if (now < transientUntil) {
      promptHud.setPrompt(transientPrompt);
    } else {
      promptHud.setPrompt(null);
    }
  }

  return {
    frame(now) {
      acc += Math.min((now - lastT) / 1000, 0.1);
      lastT = now;
      const input = simInput.sample();
      const frameEvents: SimEvent[] = [];
      while (acc >= FIXED_DT) {
        for (const e of step(state, input, FIXED_DT, level, cfg)) frameEvents.push(e);
        simInput.clearEdges();
        acc -= FIXED_DT;
      }
      if (frameEvents.length) handleEvents(frameEvents);
      const look = simInput.look();
      scene.setLook(look.x, look.y);
      scene.sync(state);
      updatePrompts(now);
      scene.render();
    },
    dispose() {
      if (switchTimer) clearTimeout(switchTimer);
      scene.dispose();
      boardPanel.hide();
      promptHud.setPrompt(null);
      promptHud.showInteract(false);
      app.classList.remove('warn-flash');
    },
  };
}

// =====================================================================
// Scene 2 — The Electric Ball Room
// =====================================================================
function startBallRoom(): SceneController {
  const cfg = DEFAULT_BALLROOM_CONFIG;
  const state = createBallRoomState();
  const scene = new BallRoomScene(engine);
  let flashUntil = 0;
  let acc = 0;
  let lastT = performance.now();
  let switchTimer: ReturnType<typeof setTimeout> | null = null;

  setControls({ wheel: true });
  sceneComplete.hide(); // clear the runner's "ENTER THE QUANTUM COMPUTER" overlay
  promptHud.setPrompt(
    'Pump the wheel in rhythm — voltage UP as they swing in — until they collide',
    true,
  );

  function handle(events: BallRoomEvent[]): void {
    for (const e of events) {
      if (e.type === 'explode') {
        flashUntil = performance.now() + 500;
        promptHud.setPrompt('The balls collide — BOOM! The way is open.', true);
        audio.boom();
      } else if (e.type === 'cleared') {
        promptHud.setPrompt('The way is clear — onward ⟶', true);
        switchTimer = setTimeout(() => setScene('catroom'), 2400);
      }
    }
  }

  return {
    frame(now) {
      acc += Math.min((now - lastT) / 1000, 0.1);
      lastT = now;
      const input = { voltage: voltageWheel.read() };
      const frameEvents: BallRoomEvent[] = [];
      while (acc >= FIXED_DT) {
        for (const e of stepBallRoom(state, input, FIXED_DT, cfg)) frameEvents.push(e);
        acc -= FIXED_DT;
      }
      if (frameEvents.length) handle(frameEvents);
      // Cut the coil hum/crackle the instant the balls blow apart.
      audio.setElectric(state.broken ? 0 : state.voltage, !state.broken && state.attracting);
      app.classList.toggle('warn-flash', now < flashUntil);
      scene.sync(state);
      scene.render();
    },
    dispose() {
      if (switchTimer) clearTimeout(switchTimer);
      audio.setElectric(0, false);
      scene.dispose();
      promptHud.setPrompt(null);
      app.classList.remove('warn-flash');
    },
  };
}

// =====================================================================
// Scene 3 — The Cat
// =====================================================================
function startCatRoom(): SceneController {
  const cfg = DEFAULT_CATROOM_CONFIG;
  const state = createCatRoomState();
  const scene = new CatRoomScene(engine);
  let acc = 0;
  let lastT = performance.now();
  let interactPressed = false;
  let resetTimer: ReturnType<typeof setTimeout> | null = null;

  setControls({ move: true, look: true });
  sceneComplete.hide();
  promptHud.setPrompt('A vast, dark room. Somewhere ahead: the way out.', true);
  interactHandler = () => {
    interactPressed = true;
  };

  function handle(events: CatRoomEvent[]): void {
    for (const e of events) {
      if (e.type === 'footstep') {
        audio.catStep();
      } else if (e.type === 'opened') {
        audio.meow();
        promptHud.setPrompt('Miaow! You observed the cat — back to the start…', true);
        resetTimer = setTimeout(() => setScene('runner'), 1600);
      } else if (e.type === 'exit') {
        promptHud.setPrompt('You slipped past without looking — the cat is still both. ⟶', true);
        resetTimer = setTimeout(() => setScene('crossing'), 2200);
      }
    }
  }

  return {
    frame(now) {
      acc += Math.min((now - lastT) / 1000, 0.1);
      lastT = now;
      const m = moveStick.value;
      const input = { move: { x: m.x, y: m.y }, interact: interactPressed };
      const frameEvents: CatRoomEvent[] = [];
      while (acc >= FIXED_DT) {
        for (const e of stepCatRoom(state, input, FIXED_DT, cfg)) frameEvents.push(e);
        input.interact = false; // edge: consume after the first sub-step
        acc -= FIXED_DT;
      }
      interactPressed = false;
      if (frameEvents.length) handle(frameEvents);
      promptHud.showInteract(state.nearButton && !state.opened, 'OPEN');
      const lk = lookStick.value;
      scene.setLook(lk.x, lk.y);
      scene.sync(state);
      scene.render();
    },
    dispose() {
      if (resetTimer) clearTimeout(resetTimer);
      scene.dispose();
      promptHud.setPrompt(null);
      promptHud.showInteract(false);
    },
  };
}

// =====================================================================
// Scene 4 — The Crossing (precessing triple pendulum)
// =====================================================================
function startCrossing(): SceneController {
  const cfg = DEFAULT_CROSSING_CONFIG;
  const state = createCrossingState();
  const scene = new CrossingScene(engine);
  let acc = 0;
  let lastT = performance.now();
  let switchTimer: ReturnType<typeof setTimeout> | null = null;

  setControls({ move: true, look: true });
  promptHud.setPrompt('Cross when the weight swings clear — read the phase, then dash.', true);

  function handle(events: CrossingEvent[]): void {
    for (const e of events) {
      if (e.type === 'footstep') {
        audio.catStep();
      } else if (e.type === 'hit') {
        audio.hit();
        promptHud.setPrompt('Clipped! Wait for the swing to clear, then go.', true);
      } else if (e.type === 'exit') {
        promptHud.setPrompt('Across — you read the phase. ⟶', true);
        switchTimer = setTimeout(() => setScene('interference'), 2600);
      }
    }
  }

  return {
    frame(now) {
      acc += Math.min((now - lastT) / 1000, 0.1);
      lastT = now;
      const m = moveStick.value;
      const input = { move: { x: m.x, y: m.y } };
      const frameEvents: CrossingEvent[] = [];
      while (acc >= FIXED_DT) {
        for (const e of stepCrossing(state, input, FIXED_DT, cfg)) frameEvents.push(e);
        acc -= FIXED_DT;
      }
      if (frameEvents.length) handle(frameEvents);
      app.classList.toggle('warn-flash', state.hitFlash > 0);
      audio.setWhoosh(Math.min(1, state.bobSpeed / 3.5));
      const lk = lookStick.value;
      scene.setLook(lk.x, lk.y);
      scene.sync(state);
      scene.render();
    },
    dispose() {
      if (switchTimer) clearTimeout(switchTimer);
      audio.setWhoosh(0);
      scene.dispose();
      promptHud.setPrompt(null);
      app.classList.remove('warn-flash');
    },
  };
}

// =====================================================================
// Scene 5 — Interference (the Nodal Walk)
// =====================================================================
function startInterference(): SceneController {
  const cfg = DEFAULT_INTERFERENCE_CONFIG;
  const state = createInterferenceState();
  const scene = new InterferenceScene(engine);
  let acc = 0;
  let lastT = performance.now();
  let switchTimer: ReturnType<typeof setTimeout> | null = null;

  setControls({ move: true, look: true });
  promptHud.setPrompt(
    'Walk the calm green lines. Twist (right stick) to shift the waves clear.',
    true,
  );

  function handle(events: InterferenceEvent[]): void {
    for (const e of events) {
      if (e.type === 'footstep') {
        audio.catStep();
      } else if (e.type === 'hit') {
        audio.hit();
        promptHud.setPrompt('An antinode threw you back — twist to a calm line, then walk.', true);
      } else if (e.type === 'exit') {
        promptHud.setPrompt('Through the calm — you cancelled the waves. ⟶', true);
        switchTimer = setTimeout(() => setScene('runner'), 2600);
      }
    }
  }

  return {
    frame(now) {
      acc += Math.min((now - lastT) / 1000, 0.1);
      lastT = now;
      const m = moveStick.value;
      const input = { move: { x: m.x, y: m.y }, phase: lookStick.value.x };
      const frameEvents: InterferenceEvent[] = [];
      while (acc >= FIXED_DT) {
        for (const e of stepInterference(state, input, FIXED_DT, cfg)) frameEvents.push(e);
        acc -= FIXED_DT;
      }
      if (frameEvents.length) handle(frameEvents);
      app.classList.toggle('warn-flash', state.hitFlash > 0);
      scene.setLook(0);
      scene.sync(state);
      scene.render();
    },
    dispose() {
      if (switchTimer) clearTimeout(switchTimer);
      scene.dispose();
      promptHud.setPrompt(null);
      app.classList.remove('warn-flash');
    },
  };
}

// --- start gate: fullscreen + landscape + gyro + audio, all on one tap ---
async function enterMachine(): Promise<void> {
  if (isFullscreenSupported()) {
    await enterFullscreen(document.documentElement);
    await lockLandscape();
  }
  await audio.start();
  if (tilt.needsPermission) {
    tiltBtn.hidden = await tilt.enable();
  } else {
    void tilt.enable();
  }
  startGate.hidden = true;
}

startGate.addEventListener('click', () => void enterMachine());
tiltBtn.addEventListener('click', async () => {
  if (await tilt.enable()) tiltBtn.hidden = true;
});

// Dev hook for the screenshot harness to force a scene/transition.
(window as unknown as { __setScene?: typeof setScene }).__setScene = setScene;

// Initial scene (?scene=ballroom / ?scene=catroom boot straight into a room).
const sceneParam = new URLSearchParams(location.search).get('scene') as SceneKind | null;
const scenes: SceneKind[] = ['runner', 'ballroom', 'catroom', 'crossing', 'interference'];
setScene(sceneParam && scenes.includes(sceneParam) ? sceneParam : 'runner');
